import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { database } from '../firebaseConfig';
import { ref, get } from 'firebase/database';
import { interestList, mergeArticles, topArticles, formatNewsDate } from '../services/nicheNews';

// Dashboard "News for you": cached articles for the agent's profile interests. Internal only.
export default function NicheNewsSection({ uid }) {
  const [interests, setInterests] = useState([]);
  const [cache, setCache] = useState({});
  const [filter, setFilter] = useState('all');
  const [loaded, setLoaded] = useState(false);
  const [tick, setTick] = useState(0);
  // Re-read when the tab regains focus: a desktop tab left open never picked up interests added on the phone.
  useEffect(() => {
    const bump = () => { if (document.visibilityState !== 'hidden') setTick((t) => t + 1); };
    window.addEventListener('focus', bump);
    document.addEventListener('visibilitychange', bump);
    return () => { window.removeEventListener('focus', bump); document.removeEventListener('visibilitychange', bump); };
  }, []);
  useEffect(() => {
    if (!uid) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const list = interestList((await get(ref(database, `users/${uid}/newsInterests`))).val());
        const entries = await Promise.all(list.map(async (i) => [i.key, (await get(ref(database, `newsCache/${i.key}`)).catch(() => null))?.val?.() || null]));
        if (!cancelled) { setInterests(list); setCache(Object.fromEntries(entries)); }
      } catch { /* leave empty */ }
      if (!cancelled) setLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [uid, tick]);
  if (!loaded) return null;
  const articles = mergeArticles(interests, cache, filter);
  const shown = topArticles(articles);
  const pill = (active) => ({ border: '1px solid #d8c9a8', background: active ? '#1a1a1a' : '#fff', color: active ? '#D4AF37' : '#1a1a1a', borderRadius: 16, padding: '6px 12px', fontSize: 13, cursor: 'pointer', margin: '0 6px 6px 0' });
  return (
    <section style={{ marginBottom: '2rem' }}>
      <h2 style={{ margin: '0 0 0.25rem 0', color: '#1a1a1a', fontSize: '1.25rem' }}>News for you</h2>
      {interests.length === 0 ? (
        <p style={{ color: '#7f7f7f', fontSize: '0.9rem' }}>
          Pick the areas and topics you care about and fresh articles will show here every couple of days. <Link to="/profile#news-interests" style={{ color: '#8C6D1F' }}>Add interests on your profile</Link>
        </p>
      ) : (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', margin: '0.5rem 0' }}>
            <button type="button" style={pill(filter === 'all')} onClick={() => setFilter('all')}>All</button>
            {interests.map((i) => <button type="button" key={i.key} style={pill(filter === i.key)} onClick={() => setFilter(i.key)}>{i.label}</button>)}
          </div>
          {shown.length === 0 ? (
            <p style={{ color: '#7f7f7f', fontSize: '0.9rem' }}>No articles yet. New ones arrive every couple of days. <Link to="/profile#news-interests" style={{ color: '#8C6D1F' }}>Edit interests</Link></p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {shown.map((a) => (
                <li key={a.link} style={{ border: '1px solid #d8c9a8', borderRadius: 8, padding: '10px 12px', marginBottom: 8, background: '#fff', overflowWrap: 'anywhere' }}>
                  <a href={a.link} target="_blank" rel="noopener noreferrer" style={{ color: '#1a1a1a', fontWeight: 600, textDecoration: 'none' }}>{a.title}</a>
                  <div style={{ color: '#7f7f7f', fontSize: '0.8rem', marginTop: 4 }}>
                    {[a.source, formatNewsDate(a.publishedAt), a.interest].filter(Boolean).join(' · ')}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
